// The Scripts IDE's memory across navigation (spec/dashboard.md sections 4.6 and 10.8): the open tabs and the unsaved
// buffers of each (game, branch), kept at module level so leaving the Extensions page, switching branch and coming back
// restores them. A saved script (its buffer equals its record's source) is not kept; it is read again when its tab opens.
// While any kept buffer is unsaved the leave guard asks before a reload or a tab close (the buffers live in memory only).
import { registerDirtySource } from '../../ui/leaveGuardRegistry';
import type { ExtensionDto } from './CreateScriptDialog';

export interface KeptScript {
  record: ExtensionDto;
  buffer: string;
}

export interface IdeMemory {
  open: string[];
  scripts: Record<string, KeptScript>;
}

const memory = new Map<string, IdeMemory>();

export const ideKey = (gameId: string, branch: string) => `${gameId}:${branch}`;

export const isUnsaved = (s: { record: ExtensionDto | null; buffer: string } | undefined): boolean => !!s?.record && s.buffer !== (s.record.source ?? '');

export function recallIde(key: string): IdeMemory | undefined {
  return memory.get(key);
}

/** Keeps the open tabs and the unsaved buffers of `key` (drops the entry when there is nothing to keep). */
export function rememberIde(key: string, open: string[], scripts: Record<string, { record: ExtensionDto | null; buffer: string }>): void {
  const kept: Record<string, KeptScript> = {};
  for (const [name, s] of Object.entries(scripts)) if (isUnsaved(s)) kept[name] = { record: s.record!, buffer: s.buffer };
  if (open.length === 0 && Object.keys(kept).length === 0) memory.delete(key);
  else memory.set(key, { open: [...open], scripts: kept });
}

/** A save that lands after the IDE unmounted: the kept buffer it saved is no longer unsaved. */
export function settleIde(key: string, name: string, savedSource: string): void {
  const m = memory.get(key);
  const s = m?.scripts[name];
  if (!m || !s || s.buffer !== savedSource) return;
  const scripts = { ...m.scripts };
  delete scripts[name];
  memory.set(key, { ...m, scripts });
}

export function anyIdeUnsaved(): boolean {
  for (const m of memory.values()) for (const s of Object.values(m.scripts)) if (isUnsaved(s)) return true;
  return false;
}

/** Tests start every case with an empty IDE. */
export function resetIdeMemory(): void {
  memory.clear();
}

registerDirtySource('ide', anyIdeUnsaved);
